import { z } from 'zod';
import { CursorQuery, Paginated } from '../../common/pagination.js';
import { GroupChatMessage, GroupChatView, SendGroupMessageInput, PinGroupMessageInput, ReadGroupChatInput, GroupChatActivityInput, GroupChatReactionInput, GroupChatVoteInput, } from '../../chat.js';
import { CommunityListQuery, CommunityBrowseQuery, CommunityView, CommunitySetupProgress, CreateCommunityInput, UpdateCommunityInput, InviteInput, JoinRequestRow, MemberRow, DirectoryPrivacyInput, DirectoryPrivacyView, CommunityMembersQuery, MembershipStateView, CommunityInvitationRow, SentCommunityInviteRow, UpdateCommunityMemberInput, CommunityMemberPosition, GroupPositionRef, GroupPositionView, CreateGroupPositionInput, UpdateGroupPositionInput, AssignGroupPositionInput, DeleteGroupPositionQuery, CommunityCalendarQuery, MeetingAttendanceQuery, MeetingAttendee, PastMeetingPhoto, CommunityMeetingView, CreateCommunityMeetingInput, UpdateCommunityMeetingInput, CommunityMeetingRsvpInput, CommunityAnnouncementView, CommunityAnnouncementQuery, CommunityAnnouncementSummary, CreateCommunityAnnouncementInput, UpdateCommunityAnnouncementInput, ReadCommunityAnnouncementsInput, } from '../../community.js';
export const communityRoutes = {
    setupProgress: {
        method: 'GET',
        path: '/communities/:communityId/setup-progress',
        response: CommunitySetupProgress,
    },
    announcements: {
        method: 'GET',
        path: '/communities/:communityId/announcements',
        query: CommunityAnnouncementQuery,
        response: Paginated(CommunityAnnouncementView),
    },
    announcementSummary: {
        method: 'GET',
        path: '/communities/:communityId/announcements/summary',
        response: CommunityAnnouncementSummary,
    },
    createAnnouncement: {
        method: 'POST',
        path: '/communities/:communityId/announcements',
        body: CreateCommunityAnnouncementInput,
        response: CommunityAnnouncementView,
    },
    updateAnnouncement: {
        method: 'PATCH',
        path: '/communities/:communityId/announcements/:announcementId',
        body: UpdateCommunityAnnouncementInput,
        response: CommunityAnnouncementView,
    },
    deleteAnnouncement: {
        method: 'DELETE',
        path: '/communities/:communityId/announcements/:announcementId',
        response: z.object({ announcementId: z.string() }),
    },
    pinAnnouncement: {
        method: 'PUT',
        path: '/communities/:communityId/announcements/:announcementId/pin',
        body: z.object({ pinned: z.boolean() }),
        response: CommunityAnnouncementSummary,
    },
    reactAnnouncement: {
        method: 'PUT',
        path: '/communities/:communityId/announcements/:announcementId/reaction',
        // Explicit desired state makes retries idempotent. Omission supports older clients.
        body: z.object({ reacted: z.boolean() }).optional(),
        response: CommunityAnnouncementView,
    },
    readAnnouncements: {
        method: 'PUT',
        path: '/communities/:communityId/announcements/read',
        body: ReadCommunityAnnouncementsInput,
        response: z.object({ unreadCount: z.number().int().nonnegative() }),
    },
    chat: {
        method: 'GET',
        path: '/communities/:communityId/chat',
        response: GroupChatView,
    },
    chatMessages: {
        method: 'GET',
        path: '/communities/:communityId/chat/messages',
        query: CursorQuery,
        response: Paginated(GroupChatMessage),
    },
    sendChatMessage: {
        method: 'POST',
        path: '/communities/:communityId/chat/messages',
        body: SendGroupMessageInput,
        response: GroupChatMessage,
    },
    chatActivity: {
        method: 'PUT',
        path: '/communities/:communityId/chat/activity',
        body: GroupChatActivityInput,
        response: z.object({ ok: z.boolean() }),
    },
    reactChatMessage: {
        method: 'PUT',
        path: '/communities/:communityId/chat/messages/:messageId/reaction',
        body: GroupChatReactionInput,
        response: GroupChatMessage,
    },
    voteChatPoll: {
        method: 'PUT',
        path: '/communities/:communityId/chat/messages/:messageId/vote',
        body: GroupChatVoteInput,
        response: GroupChatMessage,
    },
    pinChatMessage: {
        method: 'PUT',
        path: '/communities/:communityId/chat/pin',
        body: PinGroupMessageInput,
        response: GroupChatView,
    },
    readChat: {
        method: 'PUT',
        path: '/communities/:communityId/chat/read',
        body: ReadGroupChatInput,
        response: z.object({ unreadCount: z.number().int().nonnegative() }),
    },
    create: {
        method: 'POST',
        path: '/communities',
        body: CreateCommunityInput,
        response: CommunityView,
    },
    get: { method: 'GET', path: '/communities/:communityId', response: CommunityView },
    /** Edit name, imagery, visibility or taxonomy. Community admins only. */
    delete: {
        method: 'DELETE',
        path: '/communities/:communityId',
        response: z.object({ communityId: z.string() }),
    },
    update: {
        method: 'PATCH',
        path: '/communities/:communityId',
        body: UpdateCommunityInput,
        response: CommunityView,
    },
    /** My communities, Category-1 tabbed (FR-COMM-07). */
    mine: {
        method: 'GET',
        path: '/me/communities',
        query: CommunityListQuery.extend({
            includePending: z.enum(['true', 'false']).optional(),
            joinedOnly: z.enum(['true', 'false']).optional(),
        }),
        response: Paginated(CommunityView),
    },
    /** Suggested: public, not joined, ranked by mutual members (FR-COMM-08). */
    suggested: {
        method: 'GET',
        path: '/communities/suggested',
        query: CommunityListQuery,
        response: Paginated(CommunityView),
    },
    /** Public directory, taxonomy-filtered (FR-SRCH-07/08 uses q too). */
    browse: {
        method: 'GET',
        path: '/communities',
        query: CommunityBrowseQuery,
        response: Paginated(CommunityView),
    },
    join: {
        method: 'POST',
        path: '/communities/:communityId/membership',
        response: MembershipStateView,
    },
    /** Leave or cancel a request. */
    leave: {
        method: 'DELETE',
        path: '/communities/:communityId/membership',
        response: MembershipStateView,
    },
    invite: {
        method: 'POST',
        path: '/communities/:communityId/invites',
        body: InviteInput,
        response: z.object({ invited: z.number().int() }),
    },
    invitations: {
        method: 'GET',
        path: '/me/community-invitations',
        query: CommunityListQuery,
        response: Paginated(CommunityInvitationRow),
    },
    acceptInvite: {
        method: 'POST',
        path: '/communities/:communityId/invitation',
        response: MembershipStateView,
    },
    declineInvite: {
        method: 'DELETE',
        path: '/communities/:communityId/invitation',
        response: MembershipStateView,
    },
    sentInvites: {
        method: 'GET',
        path: '/communities/:communityId/invites',
        query: CursorQuery,
        response: Paginated(SentCommunityInviteRow),
    },
    revokeInvite: {
        method: 'DELETE',
        path: '/communities/:communityId/invites/:userId',
        response: z.object({ userId: z.string() }),
    },
    updateMember: {
        method: 'PATCH',
        path: '/communities/:communityId/members/:userId',
        body: UpdateCommunityMemberInput,
        response: z.object({
            userId: z.string(),
            role: z.enum(['admin', 'moderator', 'member']),
            position: CommunityMemberPosition.optional(),
            positionId: z.string().nullable().optional(),
            positionInfo: GroupPositionRef.optional(),
        }),
    },
    removeMember: {
        method: 'DELETE',
        path: '/communities/:communityId/members/:userId',
        response: z.object({ userId: z.string() }),
    },
    positions: {
        method: 'GET',
        path: '/communities/:communityId/positions',
        query: CursorQuery,
        response: Paginated(GroupPositionView).extend({ revision: z.number().int().nonnegative() }),
    },
    createPosition: {
        method: 'POST',
        path: '/communities/:communityId/positions',
        body: CreateGroupPositionInput,
        response: GroupPositionView,
    },
    updatePosition: {
        method: 'PATCH',
        path: '/communities/:communityId/positions/:positionId',
        body: UpdateGroupPositionInput,
        response: GroupPositionView,
    },
    assignPosition: {
        method: 'POST',
        path: '/communities/:communityId/positions/:positionId/assignments',
        body: AssignGroupPositionInput,
        response: z.object({ assignedCount: z.number().int().nonnegative() }),
    },
    deletePosition: {
        method: 'DELETE',
        path: '/communities/:communityId/positions/:positionId',
        query: DeleteGroupPositionQuery,
        response: z.object({ positionId: z.string(), reassignedCount: z.number().int().nonnegative() }),
    },
    directoryPrivacy: {
        method: 'GET',
        path: '/communities/:communityId/directory-privacy',
        response: DirectoryPrivacyView,
    },
    updateDirectoryPrivacy: {
        method: 'PATCH',
        path: '/communities/:communityId/directory-privacy',
        body: DirectoryPrivacyInput,
        response: DirectoryPrivacyView,
    },
    members: {
        method: 'GET',
        path: '/communities/:communityId/members',
        query: CommunityMembersQuery,
        response: Paginated(MemberRow),
    },
    // admin of the community
    requests: {
        method: 'GET',
        path: '/communities/:communityId/requests',
        query: CursorQuery,
        response: Paginated(JoinRequestRow),
    },
    approve: {
        method: 'POST',
        path: '/communities/:communityId/requests/:userId/approve',
        response: MembershipStateView,
    },
    reject: {
        method: 'DELETE',
        path: '/communities/:communityId/requests/:userId',
        response: MembershipStateView,
    },
    meetingAttendance: {
        method: 'GET',
        path: '/communities/:communityId/calendar/:meetingId/attendance',
        query: MeetingAttendanceQuery,
        response: Paginated(MeetingAttendee),
    },
    pastMeetingPhotos: {
        method: 'GET',
        path: '/communities/:communityId/calendar/photos',
        query: CursorQuery,
        response: Paginated(PastMeetingPhoto),
    },
    nextMeeting: {
        method: 'GET',
        path: '/communities/:communityId/calendar/next',
        response: CommunityMeetingView.nullable(),
    },
    calendar: {
        method: 'GET',
        path: '/communities/:communityId/calendar',
        query: CommunityCalendarQuery,
        response: z.array(CommunityMeetingView),
    },
    createMeeting: {
        method: 'POST',
        path: '/communities/:communityId/meetings',
        body: CreateCommunityMeetingInput,
        response: CommunityMeetingView,
    },
    meeting: {
        method: 'GET',
        path: '/communities/:communityId/meetings/:meetingId',
        response: CommunityMeetingView,
    },
    updateMeeting: {
        method: 'PATCH',
        path: '/communities/:communityId/meetings/:meetingId',
        body: UpdateCommunityMeetingInput,
        response: CommunityMeetingView,
    },
    deleteMeeting: {
        method: 'DELETE',
        path: '/communities/:communityId/meetings/:meetingId',
        response: z.object({ meetingId: z.string() }),
    },
    rsvpMeeting: {
        method: 'PUT',
        path: '/communities/:communityId/meetings/:meetingId/rsvp',
        body: CommunityMeetingRsvpInput,
        response: CommunityMeetingView,
    },
};
//# sourceMappingURL=routes.js.map